import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DbPortalFassung, DbPortalStandort, DbPortalStandortListe } from '@gql/shared-types';
import { api } from '../api/client.js';
import { FortschrittBalken, PrioMarke } from '../components/Fortschritt.js';
import { KonzessionaerZeitstrahl } from '../components/KonzessionaerZeitstrahl.js';
import { SkalaBlock } from '../components/SkalaAnzeige.js';
import { TYP_TEXT, firmenNamen, konzessionaerText } from '../daten/ansicht.js';
import type { Daten } from '../daten/laden.js';
import { sichtbareSkala } from '../daten/skala.js';
import { KERN_BIS, KERN_VON, formatiereDatum, formatiereMonat } from '../daten/zeitreihe.js';
import { NichtGefunden } from './NichtGefunden.js';

const MODELL_TEXT: Record<string, string> = {
  konzession: 'Konzession',
  betreiber: 'Betreibermodell',
  gemischt: 'gemischt',
  unklar: 'unklar',
};
const MODELL_FEIN_TEXT: Record<string, string> = {
  vollkonzession: 'Vollkonzession',
  teilkonzession_offen: 'Teilkonzession, offen für Dritte',
  betreiber: 'Betreibermodell',
  unklar: 'unklar',
};
const PDF_TEXT: Record<DbPortalFassung['pdf_status'], string> = {
  ok: 'vollständig',
  fehlt: 'fehlt im Bestand',
  gekappt: 'unvollständig archiviert',
  unbekannt: 'unbekannt',
};

function Gueltigkeit({ f }: { f: DbPortalFassung }) {
  if (!f.von) return <>Stand unbekannt</>;
  const von = f.von_art === 'erstnachweis' ? `spätestens ${formatiereDatum(f.von)}` : formatiereMonat(f.von);
  return (
    <>
      {von} – {f.bis ? formatiereMonat(f.bis) : 'letzte bekannte Fassung'}
    </>
  );
}

function EigeneStandorte({ slug, name }: { slug: string; name: string }) {
  const [standorte, setStandorte] = useState<DbPortalStandort[] | null>(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    api<DbPortalStandortListe>('/standorte')
      .then((l) => setStandorte(l.standorte.filter((s) => s.gebietSlug === slug)))
      .catch(() => setFehler(true));
  }, [slug]);
  return (
    <section aria-labelledby="h-standorte" className="kasten">
      <h2 id="h-standorte">Ihre Standorte in diesem Gebiet</h2>
      {fehler && <p className="fehlertext">Standorte konnten nicht geladen werden.</p>}
      {standorte && standorte.length === 0 && (
        <p>
          Ihnen ist im Gebiet {name} noch kein Standort zugeordnet. <Link to="/standorte/import">Standorte importieren</Link>
        </p>
      )}
      {standorte && standorte.length > 0 && (
        <ul className="standortliste">
          {standorte.map((s) => (
            <li key={s.id}>
              <Link to={`/standorte/${s.id}`} className="standort-eintrag">
                <PrioMarke p={s.prioritaet} />
                <span className="standort-text">
                  <span className="standort-name">{s.name}</span>
                  <span className="standort-detail">{[s.plz, s.ort].filter(Boolean).join(' ') || '–'}</span>
                </span>
                <FortschrittBalken f={s.fortschritt} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <p className="hinweis-klein">
        Mindestpaket: ein Vertrag und eine Rechnung je Standort und Jahr ({KERN_VON}–{KERN_BIS}).
      </p>
    </section>
  );
}

export function GebietSeite({ daten }: { daten: Daten }) {
  const { slug } = useParams();
  const feature = daten.gebiete.features.find((f) => f.properties.slug === slug);
  if (!feature) return <NichtGefunden />;
  const p = feature.properties;
  const fassungen = daten.fassungen.gebiete[String(p.id)] ?? [];
  const namen = firmenNamen(daten);
  const skala = sichtbareSkala(daten.skala.gebiete[p.slug]);
  const firmenImKern = new Set(
    p.zeitreihe
      .filter((e) => e.von <= `${KERN_BIS}-12-31` && (e.bis === null || e.bis > `${KERN_VON}-01-01`))
      .flatMap((e) => e.k),
  );

  return (
    <article className="seite gebiet">
      <p className="brotkrumen">
        <Link to="/">← Zur Karte</Link>
      </p>
      <header className="gebiet-kopf">
        <h1>{p.name}</h1>
        <p className="gebiet-unterzeile">
          {p.typ ? TYP_TEXT[p.typ] ?? p.typ : 'Gebiet'} · {p.bundesland ?? 'Bundesland unbekannt'} ·{' '}
          {p.fassungen === 1 ? '1 TAB-Fassung' : `${p.fassungen} TAB-Fassungen`}
        </p>
      </header>

      <div className="kennzahlen">
        <div className="kennzahl">
          <span className="kennzahl-label">
            Konzessionär {KERN_VON}–{KERN_BIS}
          </span>
          <span className="kennzahl-wert">{firmenImKern.size ? konzessionaerText([...firmenImKern], namen) : '–'}</span>
        </div>
        <div className="kennzahl">
          <span className="kennzahl-label">Erste bekannte Fassung</span>
          <span className="kennzahl-wert">{formatiereMonat(p.erste)}</span>
        </div>
        <div className="kennzahl">
          <span className="kennzahl-label">Letzte bekannte Fassung</span>
          <span className="kennzahl-wert">{formatiereMonat(p.letzte)}</span>
        </div>
      </div>

      <section aria-labelledby="h-zeit">
        <h2 id="h-zeit">Konzessionär im Zeitverlauf</h2>
        {p.zeitreihe.length ? (
          <KonzessionaerZeitstrahl zeitreihe={p.zeitreihe} namen={namen} />
        ) : (
          <p className="leer">Für dieses Gebiet ist keine datierte TAB-Fassung bekannt.</p>
        )}
      </section>

      {skala && (
        <section aria-labelledby="h-skala">
          <h2 id="h-skala">Vergleichbarkeit mit Berlin</h2>
          <SkalaBlock eintrag={skala} katalog={daten.skala.merkmale} />
        </section>
      )}

      <section aria-labelledby="h-fassungen">
        <h2 id="h-fassungen">TAB-Fassungen</h2>
        {fassungen.length === 0 ? (
          <p className="leer">Keine Fassungen erfasst.</p>
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle tabelle-karten">
              <thead>
                <tr>
                  <th scope="col">Stand</th>
                  <th scope="col">Gültig</th>
                  <th scope="col">Konzessionär</th>
                  <th scope="col">Modell</th>
                  <th scope="col">Dokument</th>
                </tr>
              </thead>
              <tbody>
                {fassungen.map((f) => (
                  <tr key={f.id}>
                    <td data-label="Stand">
                      {f.stand ?? '–'}
                      {f.stand_hinweis && <div className="zelle-hinweis">{f.stand_hinweis}</div>}
                    </td>
                    <td data-label="Gültig">
                      <Gueltigkeit f={f} />
                    </td>
                    <td data-label="Konzessionär">
                      {konzessionaerText(f.konzessionaere, namen)}
                      {f.konzessionaer_herkunft === 'uebernommen' && (
                        <div className="zelle-hinweis">aus der Vorfassung übernommen</div>
                      )}
                    </td>
                    <td data-label="Modell">
                      {f.modell ? MODELL_TEXT[f.modell] ?? f.modell : '–'}
                      {f.modell_fein && (
                        <div className="zelle-hinweis">
                          maschinell: {MODELL_FEIN_TEXT[f.modell_fein] ?? f.modell_fein}
                        </div>
                      )}
                      {f.beleg && (
                        <details className="beleg">
                          <summary>Textstelle</summary>
                          <blockquote>{f.beleg}</blockquote>
                        </details>
                      )}
                    </td>
                    <td data-label="Dokument">
                      {f.quelle_url ? (
                        <a href={f.quelle_url} target="_blank" rel="noreferrer">
                          {f.quelle_art === 'wayback' ? 'Webarchiv' : 'Quelle'}
                        </a>
                      ) : (
                        'eigene Ablage'
                      )}
                      <div className="zelle-hinweis">
                        PDF {PDF_TEXT[f.pdf_status]}
                        {f.seiten ? `, ${f.seiten} S.` : ''}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="hinweis-klein">
          Konzessionär und Modell sind maschinell aus den TAB-Texten ermittelt und nicht abschließend geprüft. Maßgeblich
          ist der Wortlaut der jeweiligen Fassung.
        </p>
      </section>

      <EigeneStandorte slug={p.slug} name={p.name} />
    </article>
  );
}
